// Field-level before → after for one audit entry, plus a raw-JSON view.
// Shared by Audit › Changes and the tenant Activity tab.

import { useId, useState } from 'react'
import type { AuditEntry } from '@/api/types'
import { formatAuditValue } from '@/lib/audit'
import { cn } from '@/lib/format'
import { ArrowRightIcon } from '@/components/ui/icons'

function Value({ v, side }: { v: unknown; side: 'before' | 'after' }) {
  const missing = v === undefined
  return (
    <span
      className={cn(
        'min-w-0 break-all rounded px-1.5 py-0.5 font-mono text-xs',
        missing ? 'italic text-subtle' : side === 'before' ? 'bg-bad-soft/60 text-ink/80 line-through decoration-bad/60' : 'bg-good-soft/70 text-ink',
      )}
    >
      {formatAuditValue(v)}
    </span>
  )
}

export function ChangeDiff({ entry }: { entry: AuditEntry }) {
  const [raw, setRaw] = useState(false)
  const rawId = useId()
  const hasRaw = entry.old_values != null || entry.new_values != null
  return (
    <div className="grid gap-2">
      {entry.changes.length ? (
        <ul className="grid gap-1.5" aria-label="Changed fields">
          {entry.changes.map((c) => (
            <li key={c.field} className="grid gap-1 text-[13px] sm:grid-cols-[minmax(8rem,auto)_1fr] sm:items-baseline sm:gap-3">
              <span className="font-mono text-xs text-muted">{c.field}</span>
              <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="sr-only">from</span>
                <Value v={c.before} side="before" />
                <ArrowRightIcon size={13} className="shrink-0 text-subtle" />
                <span className="sr-only">to</span>
                <Value v={c.after} side="after" />
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted">No field-level changes recorded.</p>
      )}
      {entry.reason ? (
        <p className="text-[13px]">
          <span className="text-muted">Reason: </span>
          {entry.reason}
        </p>
      ) : null}
      {hasRaw ? (
        <div>
          <button type="button" aria-expanded={raw} aria-controls={rawId} onClick={() => setRaw((r) => !r)} className="text-xs font-semibold text-muted underline-offset-2 hover:text-ink hover:underline">
            {raw ? 'Hide raw JSON' : 'Show raw JSON'}
          </button>
          {raw ? (
            <div id={rawId} className="mt-2 grid gap-2 sm:grid-cols-2">
              {(['old_values', 'new_values'] as const).map((k) => (
                <div key={k} className="min-w-0">
                  <p className="mb-1 text-2xs font-bold uppercase tracking-label text-subtle">{k === 'old_values' ? 'Before' : 'After'}</p>
                  <pre className="max-h-64 overflow-auto rounded-md border border-line bg-canvas p-2 font-mono text-2xs text-muted">
                    {JSON.stringify(entry[k], null, 2) ?? 'null'}
                  </pre>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
